import { Controller } from "@hotwired/stimulus"

// Shows a site favicon next to a tab's URL. The globe placeholder stays visible
// until a real icon is CONFIRMED loaded: each candidate is probed in a detached
// Image(), and the visible <img> only gets a src once a probe fires `load` with a
// non-zero natural size — so a broken/half-loaded response never flashes on screen.
// Input is debounced, and every run carries a token so stale probes from earlier
// keystrokes are ignored (no flicker while typing). Sources: DuckDuckGo → Google.
export default class extends Controller {
  static targets = ["url", "img", "globe"]
  static values = { delay: { type: Number, default: 350 } }

  connect() {
    this.token = 0
    this.hideIcon()            // globe by default until a real icon confirms
    this.resolve()             // resolve current value immediately on connect (no debounce)
  }

  // Called on every `input`; debounced so typing doesn't spawn a request per keystroke.
  update() {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.resolve(), this.delayValue)
  }

  resolve() {
    const host = this.host(this.urlTarget.value)
    const run = ++this.token   // invalidate any in-flight probes from older values
    if (!host) { this.hideIcon(); return }
    this.probe(this.candidates(host), 0, run)
  }

  candidates(host) {
    const encoded = encodeURIComponent(`https://${host}`)
    return [
      `https://icons.duckduckgo.com/ip3/${encodeURIComponent(host)}.ico`,
      `https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=${encoded}&size=32`,
    ]
  }

  probe(urls, i, run) {
    if (run !== this.token) return              // superseded by a newer keystroke
    if (i >= urls.length) { this.hideIcon(); return }
    const img = new Image()
    img.onload = () => {
      if (run !== this.token) return
      if (img.naturalWidth > 1) this.showIcon(urls[i])   // real icon → reveal it
      else this.probe(urls, i + 1, run)                  // 1px/empty → try next source
    }
    img.onerror = () => { if (run === this.token) this.probe(urls, i + 1, run) }
    img.src = urls[i]
  }

  showIcon(src) {
    this.imgTarget.src = src
    this.imgTarget.style.display = "block"
    if (this.hasGlobeTarget) this.globeTarget.style.display = "none"   // hide globe once real icon confirmed
  }

  hideIcon() {
    this.imgTarget.removeAttribute("src")
    this.imgTarget.style.display = "none"
    if (this.hasGlobeTarget) this.globeTarget.style.display = ""       // globe back as placeholder
  }

  host(value) {
    const v = (value || "").trim()
    if (!v) return null
    try {
      const h = new URL(v.includes("://") ? v : `https://${v}`).host
      // require a dotted host (a real domain) — partial/invalid input keeps the globe
      return h && h.includes(".") ? h : null
    } catch { return null }
  }

  disconnect() { clearTimeout(this.timer); this.token++ }
}
